import { useQuery } from "@tanstack/react-query";
import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { MapPin, Phone, Mail, Clock, ArrowRight, Search, Video, Sparkles, ShoppingBag, Star } from "lucide-react";
import PageHero from "@/components/PageHero";
import Seo from "@/components/Seo";
import api from "@/lib/api";
import { money } from "@/lib/format";

const HERO = "https://static.prod-images.emergentagent.com/jobs/cc147483-69b1-437e-8d93-352c289a737f/images/69c3e51b09d578df3773fc1f5bf83b8908cfaee3362d0b20a018b9936a5c6470.png";
const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function CenterCard({ l }) {
  const flagship = l.kind === "flagship";
  return (
    <div className={`eyw-card overflow-hidden transition-shadow hover:shadow-lg ${flagship ? "ring-1 ring-[#144F75]/25" : "border border-dashed border-[#D98E73]/50"}`} data-testid={`public-location-${l.id}`}>
      {(l.image || l.gallery?.[0]) && <img src={l.image || l.gallery[0]} alt={l.name} className="h-40 w-full object-cover" data-testid={`location-card-image-${l.id}`} />}
      <div className="p-6">
        <div className="flex items-center justify-between mb-1">
          {flagship ? (
            <span className="text-[11px] font-semibold px-2.5 py-0.5 rounded-full bg-[#144F75] text-white inline-flex items-center gap-1" data-testid={`badge-flagship-${l.id}`}><Star size={11} /> EYW Flagship</span>
          ) : (
            <span className="text-[11px] font-semibold px-2.5 py-0.5 rounded-full bg-[#D98E73]/20 text-[#B5794E] inline-flex items-center gap-1" data-testid={`badge-partner-${l.id}`}><Sparkles size={11} /> Network Partner</span>
          )}
        </div>
        <h3 className="font-serif text-xl text-[#1A242B] flex items-center gap-2"><MapPin size={16} className="text-[#D98E73]" /> {l.name}</h3>
        {(l.city || l.address) && <p className="text-sm text-[#4A5D6B] mt-1">{[l.address, l.city, l.state].filter(Boolean).join(", ")}</p>}
        {l.description && <p className="text-sm text-[#4A5D6B] mt-3 line-clamp-3">{l.description.replace(/<[^>]+>/g, " ")}</p>}
        <div className="mt-4 space-y-1 text-sm text-[#4A5D6B]">
          {l.phone && <p className="flex items-center gap-2"><Phone size={14} /> {l.phone}</p>}
          {l.email && <p className="flex items-center gap-2"><Mail size={14} /> {l.email}</p>}
        </div>
        {l.hours?.length > 0 && (
          <details className="mt-4">
            <summary className="text-sm text-[#144F75] cursor-pointer flex items-center gap-1.5"><Clock size={14} /> Hours</summary>
            <ul className="mt-2 text-xs text-[#4A5D6B] space-y-0.5">
              {l.hours.map((h) => (
                <li key={h.day} className="flex justify-between"><span>{DAYS[h.day]}</span><span>{h.closed ? "Closed" : `${h.open}–${h.close}`}</span></li>
              ))}
            </ul>
          </details>
        )}
        <Link to={`/locations/${l.slug}`} data-testid={`location-visit-${l.id}`} className="inline-flex items-center gap-1 text-[#144F75] text-sm font-medium mt-4 hover:gap-2 transition-all">
          Visit this center <ArrowRight size={15} />
        </Link>
      </div>
    </div>
  );
}

export default function Locations() {
  const [q, setQ] = useState("");
  const [stateF, setStateF] = useState("");
  const [cityF, setCityF] = useState("");
  const [centerF, setCenterF] = useState("");
  const [onlineOnly, setOnlineOnly] = useState(false);

  const { data: locations = [], isLoading } = useQuery({
    queryKey: ["public-locations"],
    queryFn: () => api.get("/locations").then((r) => r.data),
    refetchInterval: 60000,
  });
  const { data: spotEnhancers = [] } = useQuery({
    queryKey: ["spotlight-enhancers"],
    queryFn: () => api.get("/enhancers").then((r) => r.data),
  });
  const { data: spotProducts = [] } = useQuery({
    queryKey: ["spotlight-products"],
    queryFn: () => api.get("/products").then((r) => r.data),
  });

  const states = useMemo(() => [...new Set(locations.map((l) => l.state).filter(Boolean))].sort(), [locations]);
  const cities = useMemo(() => [...new Set(locations.filter((l) => !stateF || l.state === stateF).map((l) => l.city).filter(Boolean))].sort(), [locations, stateF]);

  const ql = q.trim().toLowerCase();
  const filtered = locations.filter((l) => {
    if (stateF && l.state !== stateF) return false;
    if (cityF && l.city !== cityF) return false;
    if (centerF && l.id !== centerF) return false;
    if (!ql) return true;
    return [l.name, l.address, l.city, l.state, l.description, l.kind].filter(Boolean).join(" ").toLowerCase().includes(ql);
  });
  // Flagships first, then partners.
  const shown = [...filtered].sort((a, b) => (a.kind === "flagship" ? -1 : 1) - (b.kind === "flagship" ? -1 : 1));
  const flagships = shown.filter((l) => l.kind === "flagship");
  const partners = shown.filter((l) => l.kind !== "flagship");

  const resetFilters = () => { setStateF(""); setCityF(""); setCenterF(""); setQ(""); setOnlineOnly(false); };

  return (
    <div data-testid="locations-page">
      <Seo
        title="Wellness Centers & Retreat Locations"
        description="Visit our Newport, Kentucky holistic wellness center and Verona, Kentucky retreat center. Full-spectrum wellness offerings, classes, events, and community."
        image={HERO}
        path="/locations"
      />
      <PageHero label="Our Centers" title="Visit us in person or online."
        subtitle="Our flagship wellness center is in Newport, Kentucky, with a retreat center in Verona. Partner centers share the same standards of care, and many sessions are also offered online."
        image={HERO} testid="locations-hero" />

      <div className="max-w-[1200px] mx-auto px-6 lg:px-10 py-16">
        {/* Cascading picker */}
        <div className="eyw-card p-5 mb-8" data-testid="find-center-filters">
          <div className="grid md:grid-cols-4 gap-3">
            <div>
              <label className="text-xs font-medium text-[#4A5D6B] mb-1 block">State</label>
              <select value={stateF} onChange={(e) => { setStateF(e.target.value); setCityF(""); setCenterF(""); }} data-testid="filter-state" className="eyw-input w-full text-sm">
                <option value="">All states</option>
                {states.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-[#4A5D6B] mb-1 block">City</label>
              <select value={cityF} onChange={(e) => { setCityF(e.target.value); setCenterF(""); }} data-testid="filter-city" className="eyw-input w-full text-sm">
                <option value="">All cities</option>
                {cities.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-[#4A5D6B] mb-1 block">Center</label>
              <select value={centerF} onChange={(e) => setCenterF(e.target.value)} data-testid="filter-center" className="eyw-input w-full text-sm">
                <option value="">All centers</option>
                {locations.filter((l) => (!stateF || l.state === stateF) && (!cityF || l.city === cityF)).map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
            </div>
            <div className="flex items-end">
              <button onClick={() => setOnlineOnly((v) => !v)} data-testid="filter-online-toggle"
                className={`w-full inline-flex items-center justify-center gap-2 rounded-full px-4 py-2.5 text-sm font-medium transition-colors ${onlineOnly ? "bg-[#9E5C7F] text-white" : "bg-white border border-[#9E5C7F]/40 text-[#9E5C7F] hover:bg-[#9E5C7F]/5"}`}>
                <Video size={15} /> Online available
              </button>
            </div>
          </div>
          <div className="flex items-center gap-2 eyw-input max-w-md w-full mt-3">
            <Search size={16} className="text-[#8FA89B]" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name, city, or state" data-testid="locations-search" className="bg-transparent outline-none flex-1 text-sm" />
          </div>
          {(stateF || cityF || centerF || q || onlineOnly) && (
            <button onClick={resetFilters} data-testid="filter-reset" className="text-xs text-[#144F75] hover:underline mt-3">Clear all filters</button>
          )}
        </div>

        {/* Online card — first-class virtual delivery */}
        <Link to="/online" data-testid="online-delivery-card"
          className={`block eyw-card overflow-hidden mb-10 group ${onlineOnly ? "ring-2 ring-[#9E5C7F]" : ""}`}>
          <div className="bg-gradient-to-r from-[#9E5C7F] to-[#7d4864] text-white p-8 flex items-center justify-between gap-4">
            <div>
              <span className="text-[11px] font-semibold px-2.5 py-0.5 rounded-full bg-white/20 inline-flex items-center gap-1"><Video size={12} /> Virtual · No address needed</span>
              <h3 className="font-serif text-2xl mt-2">Enhance your world online</h3>
              <p className="text-white/85 text-sm mt-1 max-w-xl">Book live virtual sessions, join online events and meet practitioners over Zoom or Google Meet, wherever you are.</p>
            </div>
            <span className="shrink-0 inline-flex items-center gap-1 text-sm font-medium group-hover:gap-2 transition-all">Explore online <ArrowRight size={16} /></span>
          </div>
        </Link>

        {onlineOnly ? (
          <p className="text-[#4A5D6B]" data-testid="online-only-hint">Showing online availability. <Link to="/online" className="text-[#144F75] font-medium hover:underline">Open the virtual hub →</Link></p>
        ) : isLoading ? (
          <p className="text-[#4A5D6B]">Loading centers…</p>
        ) : shown.length === 0 ? (
          <p className="text-[#4A5D6B]" data-testid="locations-empty">No centers match your filters. Questions? Call 859-979-5505 or email info@enhancingyourworld.com.</p>
        ) : (
          <div className="space-y-12">
            {flagships.length > 0 && (
              <section data-testid="flagship-centers">
                <h2 className="font-serif text-2xl text-[#1A242B] mb-5 flex items-center gap-2"><Star size={18} className="text-[#144F75]" /> Our flagship centers</h2>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {flagships.map((l) => <CenterCard key={l.id} l={l} />)}
                </div>
              </section>
            )}
            {partners.length > 0 && (
              <section data-testid="partner-centers">
                <h2 className="font-serif text-2xl text-[#1A242B] mb-5 flex items-center gap-2"><Sparkles size={18} className="text-[#D98E73]" /> Network partner centers</h2>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {partners.map((l) => <CenterCard key={l.id} l={l} />)}
                </div>
              </section>
            )}
          </div>
        )}

        {/* Enhancer spotlight */}
        {spotEnhancers.length > 0 && (
          <section className="mt-16" data-testid="enhancer-spotlight">
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-serif text-2xl text-[#1A242B] flex items-center gap-2"><Sparkles size={18} className="text-[#5C8A72]" /> Enhancer spotlight</h2>
              <Link to="/enhancers" className="text-sm text-[#144F75] hover:underline inline-flex items-center gap-1">See all <ArrowRight size={14} /></Link>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {spotEnhancers.slice(0, 4).map((en) => (
                <Link to={`/enhancers/${en.id}`} key={en.id} className="eyw-card p-6 text-center hover:shadow-lg transition-shadow" data-testid={`spotlight-enhancer-${en.id}`}>
                  {en.avatar ? <img src={en.avatar} alt={en.name} className="h-20 w-20 rounded-full object-cover mx-auto mb-3" /> : <div className="h-20 w-20 rounded-full bg-[#144F75]/10 grid place-items-center mx-auto mb-3"><Sparkles className="text-[#144F75]" /></div>}
                  <h3 className="font-serif text-lg text-[#1A242B]">{en.name}</h3>
                  {en.headline && <p className="text-xs text-[#4A5D6B] mt-1 line-clamp-2">{en.headline}</p>}
                  <span className="mt-3 inline-block text-sm font-semibold text-[#144F75]" data-testid={`enhancer-card-cta-${en.id}`}>View profile & book →</span>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* Marketplace spotlight */}
        {spotProducts.length > 0 && (
          <section className="mt-16" data-testid="marketplace-spotlight">
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-serif text-2xl text-[#1A242B] flex items-center gap-2"><ShoppingBag size={18} className="text-[#B5794E]" /> From the marketplace</h2>
              <Link to="/marketplace" className="text-sm text-[#144F75] hover:underline inline-flex items-center gap-1">Shop all <ArrowRight size={14} /></Link>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {spotProducts.slice(0, 4).map((p) => (
                <Link to={`/marketplace/${p.slug || p.id}`} key={p.id} className="eyw-card overflow-hidden hover:shadow-lg transition-shadow" data-testid={`spotlight-product-${p.id}`}>
                  {p.image && <img src={p.image} alt={p.title} className="h-36 w-full object-cover" />}
                  <div className="p-5">
                    <h3 className="text-[#1A242B] font-medium line-clamp-1">{p.title}</h3>
                    <span className="text-[#144F75] font-serif mt-2 inline-block">{money(Number(p.price || 0))}</span>
                    <span className="block mt-2 text-xs font-semibold text-[#B5794E]" data-testid={`spotlight-product-cta-${p.id}`}>View item →</span>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
